import React, { useState, useRef, useEffect, useCallback } from "react";
import { toast, ToastContainer } from "react-toastify";
import {
  LuScanLine, LuSearch, LuX, LuMinus, LuPlus, LuRefreshCw, LuCheck,
} from "react-icons/lu";
import requestApi, { get } from "../../../apirest";
import { routeZblInput } from "../../utils/codebars/ValidateZPL";
import { getLoggedUser } from "../../../utils/userUtils";
import { useAuth } from "../../../context/auth";

const formatTime = (date) => {
  const h = String(date.getHours()).padStart(2, "0");
  const m = String(date.getMinutes()).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  const mo = String(date.getMonth() + 1).padStart(2, "0");
  return `${h}:${m} • ${d}/${mo}/${date.getFullYear()}`;
};

export default function StockCount() {
  const { userAuth } = useAuth();
  const [inputValue, setInputValue] = useState("");
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const inputRef = useRef(null);
  const userRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const getUser = useCallback(async () => {
    if (!userRef.current && userAuth) {
      userRef.current = await getLoggedUser(userAuth.uid);
    }
    return userRef.current;
  }, [userAuth]);

  const handleScan = async (raw) => {
    if (!raw.trim()) return;
    const parsed = routeZblInput(raw.trim());
    if (parsed.type !== "ZBL_TAG") {
      toast.warning("Formato inválido. Bipe uma etiqueta com tamanho (ex: 17696PP).");
      return;
    }

    setScanning(true);
    try {
      const resp = await get(`/products/findByBarcode`, { code128: parsed.raw });
      const { product, variant, stock } = resp.data;

      setItems((prev) => {
        const existing = prev.findIndex((i) => i.stockId === stock._id);
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = {
            ...updated[existing],
            countedQty: updated[existing].countedQty + 1,
            lastScanned: new Date(),
          };
          return updated;
        }
        return [
          {
            stockId: stock._id,
            productName: product?.name || "—",
            variantName: variant?.name || null,
            color: variant?.colorName || null,
            image: variant?.pictures?.[0]?.url || null,
            size: stock.size,
            originalQty: stock.quantity,
            countedQty: 1,
            code128: stock.code128,
            lastScanned: new Date(),
          },
          ...prev,
        ];
      });
    } catch {
      toast.error("Código não encontrado no estoque.");
    } finally {
      setScanning(false);
      setInputValue("");
      if (inputRef.current) inputRef.current.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleScan(inputValue);
  };

  const changeQty = (stockId, delta) => {
    setItems((prev) =>
      prev.map((i) =>
        i.stockId === stockId
          ? { ...i, countedQty: Math.max(0, i.countedQty + delta), lastScanned: new Date() }
          : i
      )
    );
  };

  const setQty = (stockId, value) => {
    const qty = parseInt(value, 10);
    if (isNaN(qty) || qty < 0) return;
    setItems((prev) =>
      prev.map((i) => (i.stockId === stockId ? { ...i, countedQty: qty } : i))
    );
  };

  const removeItem = (stockId) =>
    setItems((prev) => prev.filter((i) => i.stockId !== stockId));

  const handleCancel = () => {
    setItems([]);
    setInputValue("");
    setSearch("");
    if (inputRef.current) inputRef.current.focus();
  };

  const handleSave = async () => {
    if (items.length === 0) return;
    setSaving(true);
    try {
      const user = await getUser();
      await Promise.all(
        items.map(async (item) => {
          const stock = {
            _id: item.stockId,
            quantity: item.countedQty,
            lastChangeUser: user,
          };
          await requestApi("/stocks", { stock }, "patch");
          await requestApi(
            "/logStocks",
            {
              logStock: {
                event: "Contagem de Estoque",
                modified: `Estoque ajustado de ${item.originalQty} para ${item.countedQty} (tam: ${item.size})`,
                stock,
              },
            },
            "post"
          );
        })
      );
      toast.success("Estoque atualizado com sucesso!");
      setItems([]);
      if (inputRef.current) inputRef.current.focus();
    } catch {
      toast.error("Erro ao salvar. Tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  const filtered = items.filter(
    (i) =>
      !search ||
      i.productName?.toLowerCase().includes(search.toLowerCase()) ||
      i.code128?.toLowerCase().includes(search.toLowerCase()) ||
      i.size?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", paddingBottom: items.length > 0 ? 88 : 32 }}>
      <ToastContainer />

      {/* Header */}
      <div style={{ background: "#fff", borderBottom: "1px solid #efefef", padding: "28px 32px 24px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
              <h4 style={{ margin: 0, fontWeight: 700, fontSize: 22, color: "#111" }}>
                Contagem de Estoque
              </h4>
              {items.length > 0 && (
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  background: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0",
                  borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 600,
                }}>
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#16a34a", display: "inline-block" }} />
                  Em andamento
                </span>
              )}
            </div>
            <p style={{ margin: 0, color: "#74818e", fontSize: 13.5 }}>
              Bipe ou digite o código de barras para registrar os itens contados.
            </p>
          </div>

          {items.length > 0 && (
            <div style={{
              background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 14,
              padding: "12px 18px", display: "flex", alignItems: "center", gap: 14, minWidth: 200,
            }}>
              <div style={{
                width: 40, height: 40, borderRadius: 10, background: "#eff2f7",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <LuRefreshCw size={18} color="#214a4c" />
              </div>
              <div>
                <p style={{ margin: 0, fontSize: 11, color: "#9ca3af", fontWeight: 500 }}>Contados</p>
                <p style={{ margin: "2px 0 6px", fontWeight: 700, fontSize: 20, color: "#111", lineHeight: 1 }}>
                  {items.length}
                  <span style={{ fontSize: 13, color: "#9ca3af", fontWeight: 400 }}> itens</span>
                </p>
                <div style={{ height: 4, background: "#e5e7eb", borderRadius: 4, overflow: "hidden" }}>
                  <div style={{
                    height: "100%", background: "#214a4c", borderRadius: 4,
                    width: `${Math.min(100, items.length * 5)}%`, transition: "width 0.3s",
                  }} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Scan input */}
        <div style={{ display: "flex", gap: 12, marginTop: 20, maxWidth: 520 }}>
          <div style={{ position: "relative", flex: 1 }}>
            <LuScanLine size={16} color="#9ca3af" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
            <input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Bipe o código de barras..."
              disabled={scanning || saving}
              style={{
                width: "100%", padding: "10px 12px 10px 36px",
                border: "1.5px solid #e5e7eb", borderRadius: 10, fontSize: 14,
                outline: "none", background: "#fff",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              }}
            />
          </div>
          <button
            onClick={() => handleScan(inputValue)}
            disabled={scanning || saving}
            style={{
              padding: "10px 20px", background: "#111", color: "#fff",
              border: "none", borderRadius: 10, fontSize: 13.5, fontWeight: 600,
              cursor: "pointer", whiteSpace: "nowrap",
            }}
          >
            {scanning ? "..." : "Escanear"}
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: "24px 32px" }}>
        {items.length > 0 && (
          <div style={{ position: "relative", marginBottom: 20, maxWidth: 520 }}>
            <LuSearch size={15} color="#9ca3af" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar produto, referência ou código"
              style={{
                width: "100%", padding: "9px 12px 9px 34px",
                border: "1.5px solid #e5e7eb", borderRadius: 10, fontSize: 13.5,
                outline: "none", background: "#fff",
              }}
            />
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {filtered.map((item) => {
            const diff = item.countedQty - item.originalQty;
            const diffColor = diff > 0 ? "#16a34a" : diff < 0 ? "#dc2626" : "#9ca3af";
            const diffBg    = diff > 0 ? "#f0fdf4"  : diff < 0 ? "#fef2f2"  : "#f4f4f5";
            const diffText  = diff === 0
              ? "Sem alteração no estoque."
              : diff === 1
              ? "Foi adicionado 1 produto ao estoque."
              : diff > 1
              ? `Foram adicionados ${diff} produtos ao estoque.`
              : diff === -1
              ? "Foi removido 1 produto do estoque."
              : `Foram removidos ${Math.abs(diff)} produtos do estoque.`;

            return (
              <div
                key={item.stockId}
                style={{
                  background: "#fff", borderRadius: 14, border: "1.5px solid #e5e7eb",
                  display: "flex", alignItems: "stretch",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                  overflow: "hidden", position: "relative",
                }}
              >
                {/* Botão remover — canto superior direito */}
                <button
                  onClick={() => removeItem(item.stockId)}
                  style={{
                    position: "absolute", right: 10, top: 10, zIndex: 1,
                    background: "#f4f4f5", border: "none", borderRadius: "50%",
                    width: 24, height: 24, cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "#9ca3af",
                  }}
                  title="Remover"
                >
                  <LuX size={12} />
                </button>

                {/* Imagem */}
                <div style={{ width: 100, minHeight: 120, background: "#f4f4f5", flexShrink: 0 }}>
                  {item.image
                    ? <img src={item.image} alt={item.productName} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                    : <div style={{ width: "100%", height: "100%", minHeight: 120, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, color: "#d1d5db" }}>📦</div>
                  }
                </div>

                {/* Info central */}
                <div style={{ flex: 1, padding: "16px 16px 16px 18px", display: "flex", flexDirection: "column", justifyContent: "center", minWidth: 0, gap: 2 }}>
                  {item.code128 && (
                    <p style={{ margin: 0, fontSize: 10.5, color: "#b0b8c4", fontWeight: 500, letterSpacing: "0.04em", textTransform: "uppercase" }}>
                      REF. {item.code128}
                    </p>
                  )}
                  <p style={{ margin: "3px 0 6px", fontWeight: 700, fontSize: 14.5, color: "#111", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: 28 }}>
                    {item.productName}
                  </p>

                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 4 }}>
                    {item.color && (
                      <span style={{ fontSize: 12.5, color: "#5a6474" }}>
                        Cor: <strong style={{ color: "#111", fontWeight: 600 }}>{item.color}</strong>
                      </span>
                    )}
                    <span style={{ fontSize: 12.5, color: "#5a6474" }}>
                      Tamanho: <strong style={{ color: "#111", fontWeight: 600 }}>{item.size}</strong>
                    </span>
                  </div>

                  {/* Bloco de estoque */}
                  <div style={{ marginTop: 6, paddingTop: 8, borderTop: "1px solid #f0f0f0" }}>
                    <p style={{ margin: "0 0 1px", fontSize: 12, color: "#6b7280" }}>
                      Estoque atual: <strong style={{ color: "#111" }}>{item.originalQty} {item.originalQty === 1 ? "produto" : "produtos"}</strong>
                      {"  ·  "}
                      Contagem: <strong style={{ color: "#111" }}>{item.countedQty} {item.countedQty === 1 ? "produto" : "produtos"}</strong>
                    </p>
                    {diff !== 0 && (
                      <p style={{ margin: "3px 0 0", fontSize: 12, color: diffColor, fontWeight: 500 }}>
                        {diffText}
                      </p>
                    )}
                  </div>
                </div>

                {/* Stepper */}
                <div style={{
                  padding: "16px 20px 16px 12px", display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center", gap: 8, flexShrink: 0,
                }}>
                  <p style={{ margin: 0, fontSize: 10.5, color: "#9ca3af", fontWeight: 500, whiteSpace: "nowrap" }}>
                    Quantidade contada
                  </p>
                  <div style={{ display: "flex", alignItems: "center", border: "1.5px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
                    <button
                      onClick={() => changeQty(item.stockId, -1)}
                      style={{ width: 36, height: 36, background: "#fff", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280" }}
                    >
                      <LuMinus size={13} />
                    </button>
                    <input
                      type="number"
                      min="0"
                      value={item.countedQty}
                      onChange={(e) => setQty(item.stockId, e.target.value)}
                      style={{
                        width: 48, height: 36, border: "none",
                        borderLeft: "1.5px solid #e5e7eb", borderRight: "1.5px solid #e5e7eb",
                        textAlign: "center", fontSize: 15, fontWeight: 700, color: "#111", outline: "none",
                      }}
                    />
                    <button
                      onClick={() => changeQty(item.stockId, 1)}
                      style={{ width: 36, height: 36, background: "#111", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff" }}
                    >
                      <LuPlus size={13} />
                    </button>
                  </div>
                  <p style={{ margin: 0, fontSize: 10.5, color: "#b0b8c4", textAlign: "center" }}>
                    {formatTime(item.lastScanned)}
                  </p>
                </div>
              </div>
            );
          })}

          {items.length === 0 && (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#9ca3af" }}>
              <LuScanLine size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
              <p style={{ fontSize: 14, margin: 0 }}>Nenhum item escaneado ainda.</p>
              <p style={{ fontSize: 13, margin: "4px 0 0" }}>
                Bipe um código de barras no campo acima para começar.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Bottom sticky bar */}
      {items.length > 0 && (
        <div style={{
          position: "fixed", bottom: 0, right: 0,
          left: 240,
          background: "#fff", borderTop: "1.5px solid #e5e7eb",
          padding: "14px 32px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          zIndex: 100,
        }}
          className="stock-bottom-bar"
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, background: "#eff2f7",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <LuRefreshCw size={16} color="#214a4c" />
            </div>
            <div>
              <p style={{ margin: 0, fontSize: 11, color: "#9ca3af" }}>Produtos contados</p>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#111" }}>
                {items.length} {items.length === 1 ? "item" : "itens"}
              </p>
            </div>
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            <button
              onClick={handleCancel}
              disabled={saving}
              style={{
                padding: "10px 20px", background: "none", border: "none",
                color: "#5a6474", fontSize: 14, fontWeight: 600, cursor: "pointer",
              }}
            >
              Cancelar contagem
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: "10px 24px", background: "#111", color: "#fff",
                border: "none", borderRadius: 12, fontSize: 14, fontWeight: 600,
                cursor: "pointer", display: "flex", alignItems: "center", gap: 8,
              }}
            >
              {saving ? "Salvando..." : (
                <>Atualizar estoque <LuCheck size={16} /></>
              )}
            </button>
          </div>

          <style>{`
            @media (max-width: 768px) {
              .stock-bottom-bar { left: 0 !important; padding: 12px 16px !important; }
            }
          `}</style>
        </div>
      )}
    </div>
  );
}
